// eslint-disable-next-line simple-import-sort/imports
import { useEffect, useState } from "react";
import _ from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import config from "@/config";
import useGetSearchParams from "@/hooks/useGetSearchParams";
import useNavigateToArtwork from "@/hooks/useNavigateToArtwork";
import useQuery from "@/hooks/useQuery";
import Text from "@/shared-components/text/components/Text";
import AppNavigationPage from "@/shared-components/wrappers/components/AppNavigationPage";
import Helmet from "react-helmet";
import Loader from "@/shared-components/loader/Loader";

import HomeHeader from "../components/HomeHeader";

import VirtualList from "./components/VirtualList";

import "./index.css";
import fetchWrapper from "@/services/fetchWrapper.service";

export default function CollectionPage({ collectionEndpoint = null, collName = null, additionalParams = {}, title, metadescription }) {
  const pas = useParams();
  const { collection_id, search_type, search_item } = pas;
  const navigate = useNavigate();
  const [params, setParams] = useGetSearchParams({ validParams: ["sort"] });
  const [query, setQuery] = useState({
    sort:
      ["theme", "emotion", "color"].indexOf(search_type) !== -1
        ? "Default"
        : "Relevant",
    q: "",
    [search_type]: search_item?.replace(/-/g, " "),
    ...additionalParams,
    ...params,
  });

  const [limitedEditionData, setLimitedEditionData] = useState(null);
  const [collectionName, setCollectionName] = useState("");
  const { loading, queryData, loadMoreData } = useQuery(
    `${collName || collection_id}_collection_page`,
    `${config.apis.api.url}${collectionEndpoint || `/category/${collection_id}`}`,
    query,
    {
      injectToken: true,
    }
  );
  const [searchType, setSearchType] = useState(search_type);
  const navigateToArtwork = useNavigateToArtwork(
    `${collName || collection_id}_collection_page`,
    queryData?.data,
    `${collectionEndpoint || `/category/${collection_id}`}${queryData?.queryString}`
  );

  function onSort(type) {
    const newQuery = { ...query, sort: type };
    setQuery(newQuery);
    setParams(newQuery, { replace: true });
  }

  function onInput(value) {
    const newQuery = { ...query, q: value };
    setQuery(newQuery);
    setParams(newQuery, { replace: true });
  }

  useEffect(() => {
    if (additionalParams.price) {
      setSearchType("price");
    }
    if (additionalParams.size) {
      setSearchType("size");
    }
  }, [additionalParams, collectionName]);

  useEffect(() => {
    if (search_type) {
      setCollectionName(search_item.replace(/-/g, " "));
      return;
    }
    if (collName) {
      setCollectionName(collName);
      return;
    }
    if (!collection_id) return;
    const core = async () => {
      const { response, data } = await fetchWrapper.get(
        `${config.apis.api.url}/home/name/${collection_id}`,
        {
          injectToken: true,
        }
      );
      if (response.ok) {
        setCollectionName(data.name);
      }
    };
    core();
  }, [collection_id, collName, search_type, search_item]);

  useEffect(() => {
    if (!collectionEndpoint && (!collection_id || collection_id.length === 0)) navigate("/");
    if (collName === "Limited Editions" && queryData) {
      let deepCopiedQueryData = _.cloneDeep(queryData);
      deepCopiedQueryData.data.data = deepCopiedQueryData.data.data.map((item) => {
        let newItem = { ...item };
        newItem.artwork_about.artwork_price = config.variables.LIMITED_EDITION_SMALL_PRICE;
        newItem.artwork_about.artwork_currency = "EUR";
        return newItem;
      });
      setLimitedEditionData(deepCopiedQueryData);
    }
  }, [queryData, pas, collectionEndpoint]);

  return (
    <>
      <Helmet>
        <title>{title || `${collectionName} | Suarte`}</title>
        <meta name="description" content={metadescription || `A collection of ${collectionName} featured by Suarte`} />
      </Helmet>
      <AppNavigationPage>
        <div className="collection-page__page">
          <HomeHeader
            onInput={onInput}
            value={query.q}
            text={collectionName}
            placeholder="Search for artworks..."
            type={searchType}
            onSort={onSort}
            defaultOption={query.sort}
          />

          {loading ? (
            <Loader />
          ) : queryData.data.data.length === 0 ? (
            <Text className="collection-page__empty-text" paragraph medium>
              No artworks available for your search query.
            </Text>
          ) : collectionName === "Limited Editions" ? (
            limitedEditionData && (
              <VirtualList
                key={limitedEditionData.queryString}
                items={limitedEditionData.data}
                onLoadMore={loadMoreData}
                navigateToArtwork={navigateToArtwork}
                collectionName={collectionName}
              />
            )
          ) : (
            <VirtualList
              key={queryData.queryString}
              items={queryData.data}
              onLoadMore={loadMoreData}
              navigateToArtwork={navigateToArtwork}
            />
          )}
        </div>
      </AppNavigationPage>
    </>
  );
}
